import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PushSettings } from "../components/pwa/PushSettings";
import { PushLogList } from "../components/pwa/PushLogList";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, KeyRound, Smartphone, MonitorSmartphone, LogOut, Download,
  DatabaseBackup, RotateCcw, Save, Lock,
} from "lucide-react";

export default function SecurityPage() {
  const { token, user, isAdmin, isManager, refreshUser } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [twoFAModal, setTwoFAModal] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState("");
  const [disableModal, setDisableModal] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [sessions, setSessions] = useState([]);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [ipText, setIpText] = useState("");
  const [backups, setBackups] = useState([]);
  const [creatingBackup, setCreatingBackup] = useState(false);

  useEffect(() => { fetchSessions(); }, [showAllSessions]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isManager) fetchPolicy();
    if (isAdmin) fetchBackups();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API_URL}/security/sessions?all=${showAllSessions}`, { headers, withCredentials: true });
      setSessions(res.data);
    } catch { toast.error("Erro ao carregar sessões"); }
  };

  const fetchPolicy = async () => {
    try {
      const res = await axios.get(`${API_URL}/security/settings`, { headers, withCredentials: true });
      setPolicy(res.data);
      setIpText((res.data.ip_whitelist || []).join("\n"));
    } catch { /* sem permissão */ }
  };

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API_URL}/security/backups`, { headers, withCredentials: true });
      setBackups(res.data);
    } catch { /* sem permissão */ }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { toast.error("As palavras-passe não coincidem"); return; }
    try {
      await axios.post(`${API_URL}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { headers, withCredentials: true });
      toast.success("Palavra-passe alterada com sucesso");
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao alterar"); }
  };

  const start2FASetup = async () => {
    try {
      const res = await axios.post(`${API_URL}/security/2fa/setup`, {}, { headers, withCredentials: true });
      setTwoFASetup(res.data);
      setTwoFACode("");
      setTwoFAModal(true);
    } catch { toast.error("Erro ao iniciar 2FA"); }
  };

  const enable2FA = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/security/2fa/enable`, { code: twoFACode }, { headers, withCredentials: true });
      toast.success("2FA ativado! Será pedido o código no próximo login.");
      setTwoFAModal(false);
      refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Código inválido"); }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/security/2fa/disable`, { password: disablePw }, { headers, withCredentials: true });
      toast.success("2FA desativado");
      setDisableModal(false); setDisablePw("");
      refreshUser();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const revokeSession = async (s) => {
    if (!window.confirm("Terminar esta sessão?")) return;
    try {
      await axios.delete(`${API_URL}/security/sessions/${s.session_id}`, { headers, withCredentials: true });
      toast.success("Sessão terminada");
      fetchSessions();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const savePolicy = async () => {
    try {
      await axios.put(`${API_URL}/security/settings`, {
        min_length: parseInt(policy.min_length) || 6,
        require_upper: policy.require_upper,
        require_number: policy.require_number,
        require_symbol: policy.require_symbol,
        ip_whitelist: ipText.split("\n").map((s) => s.trim()).filter(Boolean),
      }, { headers, withCredentials: true });
      toast.success("Políticas de segurança guardadas");
      fetchPolicy();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao guardar"); }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      const res = await axios.post(`${API_URL}/security/backups`, {}, { headers, withCredentials: true });
      toast.success(`Backup criado (${res.data.total_docs} documentos)`);
      fetchBackups();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar backup"); }
    finally { setCreatingBackup(false); }
  };

  const downloadBackup = async (b) => {
    try {
      const res = await axios.get(`${API_URL}/security/backups/${b.backup_id}/download`, { headers, withCredentials: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${(b.created_at || "").slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao descarregar"); }
  };

  const restoreBackup = async (b) => {
    const confirm = window.prompt(`ATENÇÃO: isto substitui TODOS os dados da empresa pelos dados do backup de ${formatDate(b.created_at)}.\nÉ criado um snapshot de segurança antes.\n\nEscreva RESTAURAR para confirmar:`);
    if (confirm === null) return;
    try {
      await axios.post(`${API_URL}/security/backups/${b.backup_id}/restore`, { confirm }, { headers, withCredentials: true });
      toast.success("Dados restaurados com sucesso. Recarregue a página.");
      fetchBackups();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao restaurar"); }
  };

  return (
    <div className="space-y-6" data-testid="security-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Segurança</h1>
        <p className="text-muted-foreground">2FA, sessões, políticas de password, controlo de IP e backups</p>
      </div>

      <Tabs defaultValue="account">
        <TabsList>
          <TabsTrigger value="account" data-testid="security-tab-account">A Minha Conta</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="security-tab-sessions">Sessões</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="security-tab-notifications">Notificações</TabsTrigger>
          {isManager && <TabsTrigger value="policies" data-testid="security-tab-policies">Políticas & IP</TabsTrigger>}
          {isAdmin && <TabsTrigger value="backups" data-testid="security-tab-backups">Backups</TabsTrigger>}
        </TabsList>

        <TabsContent value="account" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Alterar Palavra-passe</p>
              <form onSubmit={changePassword} className="space-y-3">
                <div className="space-y-1"><Label>Palavra-passe Atual *</Label><Input required type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="current-password-input" /></div>
                <div className="space-y-1"><Label>Nova Palavra-passe *</Label><Input required type="password" value={pwForm.newPw} onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })} data-testid="new-password-input" /></div>
                <div className="space-y-1"><Label>Confirmar Nova *</Label><Input required type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="confirm-password-input" /></div>
                <Button type="submit" data-testid="change-password-btn"><Save className="w-4 h-4 mr-2" /> Alterar</Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-primary" /> Autenticação 2 Fatores (2FA)</p>
                <Badge className={user?.two_fa_enabled ? "bg-emerald-500/20 text-emerald-600" : "bg-slate-500/20 text-slate-500"} data-testid="twofa-status-badge">
                  {user?.two_fa_enabled ? "Ativo" : "Inativo"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Proteja a sua conta com uma app autenticadora (Google Authenticator, Authy, Microsoft Authenticator). Após ativar, será pedido um código de 6 dígitos em cada login.
              </p>
              {user?.two_fa_enabled ? (
                <Button variant="outline" className="text-destructive" onClick={() => setDisableModal(true)} data-testid="disable-2fa-btn">Desativar 2FA</Button>
              ) : (
                <Button onClick={start2FASetup} data-testid="setup-2fa-btn"><ShieldCheck className="w-4 h-4 mr-2" /> Configurar 2FA</Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4 space-y-4">
          <PushSettings isManager={isManager} />
          {isManager && <PushLogList />}
        </TabsContent>

        <TabsContent value="sessions" className="mt-4 space-y-3">
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Switch checked={showAllSessions} onCheckedChange={setShowAllSessions} data-testid="all-sessions-switch" />
              <Label>Ver todas as sessões da empresa</Label>
            </div>
          )}
          <div className="space-y-2">
            {sessions.map((s, i) => (
              <Card key={s.session_id || i}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MonitorSmartphone className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium">{s.user_name || user?.name} {s.ip ? `· IP ${s.ip}` : ""}{s.current && <span className="ml-2 text-[10px] uppercase tracking-wide rounded-full bg-emerald-500/15 text-emerald-600 px-2 py-0.5" data-testid="current-session-badge">Esta sessão</span>}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-md">{s.user_agent || "Dispositivo desconhecido"} · início {formatDate(s.created_at)}</p>
                    </div>
                  </div>
                  {s.session_id && !s.current && (
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => revokeSession(s)} data-testid={`revoke-session-${s.session_id}`}>
                      <LogOut className="w-4 h-4 mr-1" /> Terminar
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
            {sessions.length === 0 && <Card><CardContent className="py-8 text-center text-muted-foreground">Sem sessões registadas</CardContent></Card>}
          </div>
        </TabsContent>

        {isManager && (
          <TabsContent value="policies" className="mt-4 grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-primary" /> Política de Passwords</p>
                {policy && (
                  <>
                    <div className="space-y-1">
                      <Label>Tamanho mínimo</Label>
                      <Input type="number" min="4" max="64" value={policy.min_length} onChange={(e) => setPolicy({ ...policy, min_length: e.target.value })} disabled={!isAdmin} data-testid="min-length-input" />
                    </div>
                    <div className="flex items-center justify-between"><Label>Exigir letra maiúscula</Label><Switch checked={policy.require_upper} onCheckedChange={(v) => setPolicy({ ...policy, require_upper: v })} disabled={!isAdmin} data-testid="require-upper-switch" /></div>
                    <div className="flex items-center justify-between"><Label>Exigir número</Label><Switch checked={policy.require_number} onCheckedChange={(v) => setPolicy({ ...policy, require_number: v })} disabled={!isAdmin} /></div>
                    <div className="flex items-center justify-between"><Label>Exigir símbolo</Label><Switch checked={policy.require_symbol} onCheckedChange={(v) => setPolicy({ ...policy, require_symbol: v })} disabled={!isAdmin} /></div>
                  </>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="font-semibold">Controlo de IP (lista de IPs permitidos)</p>
                <p className="text-xs text-muted-foreground">
                  Uma regra por linha: IP exacto (<span className="font-mono">105.172.10.25</span>), intervalo (<span className="font-mono">105.172.0.0/16</span>) ou prefixo (<span className="font-mono">105.172.*</span>). Se a lista estiver vazia, todos os IPs podem entrar.
                  {policy?.client_ip && <> O IP real deste dispositivo é <strong className="font-mono" data-testid="security-client-ip">{policy.client_ip}</strong>.</>}
                </p>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2" data-testid="ip-whitelist-warning">
                  Atenção: muitas ligações em Angola têm IP dinâmico (muda sem aviso). Prefira intervalos ou prefixos em vez de IPs exactos. Os <strong>administradores nunca ficam bloqueados</strong> — entram sempre (com alerta de segurança) para poderem corrigir a lista; os restantes utilizadores são bloqueados fora da lista.
                </p>
                <Textarea rows={5} value={ipText} onChange={(e) => setIpText(e.target.value)} placeholder={"Ex: 105.172.10.25\n105.172.0.0/16\n197.231.*"} disabled={!isAdmin} data-testid="ip-whitelist-textarea" />
                {isAdmin && <Button onClick={savePolicy} data-testid="save-security-settings-btn"><Save className="w-4 h-4 mr-2" /> Guardar Políticas</Button>}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="backups" className="mt-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm text-muted-foreground">É criado um backup automático diário no primeiro login do dia. Os últimos 7 automáticos são mantidos.</p>
              <Button onClick={createBackup} disabled={creatingBackup} data-testid="create-backup-btn">
                <DatabaseBackup className="w-4 h-4 mr-2" /> {creatingBackup ? "A criar..." : "Criar Backup Agora"}
              </Button>
            </div>
            <div className="space-y-2">
              {backups.map((b) => (
                <Card key={b.backup_id} data-testid={`backup-${b.backup_id}`}>
                  <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2">{formatDate(b.created_at)} <Badge variant="outline">{b.auto ? "Automático" : "Manual"}</Badge></div>
                      <p className="text-xs text-muted-foreground">{b.total_docs} documentos · por {b.created_by}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => downloadBackup(b)} data-testid={`download-backup-${b.backup_id}`}>
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => restoreBackup(b)} data-testid={`restore-backup-${b.backup_id}`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Restaurar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {backups.length === 0 && <Card><CardContent className="py-8 text-center text-muted-foreground">Ainda sem backups — crie o primeiro agora</CardContent></Card>}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={twoFAModal} onOpenChange={setTwoFAModal}>
        <DialogContent className="max-w-sm" data-testid="twofa-setup-modal">
          <DialogHeader>
            <DialogTitle>Configurar 2FA</DialogTitle>
            <DialogDescription>Proteja a sua conta com uma app autenticadora</DialogDescription>
          </DialogHeader>
          {twoFASetup && (
            <form onSubmit={enable2FA} className="space-y-3">
              <p className="text-sm text-muted-foreground">1. Abra a app autenticadora e leia este QR code:</p>
              <div className="flex justify-center"><img src={twoFASetup.qr_code} alt="QR 2FA" className="w-44 h-44 border rounded-lg" /></div>
              <p className="text-xs text-muted-foreground text-center">Ou introduza manualmente: <span className="font-mono font-bold">{twoFASetup.secret}</span></p>
              <p className="text-sm text-muted-foreground">2. Introduza o código de 6 dígitos gerado:</p>
              <Input required inputMode="numeric" maxLength={6} placeholder="000000" className="text-center text-xl font-mono tracking-widest" value={twoFACode} onChange={(e) => setTwoFACode(e.target.value)} data-testid="twofa-code-input" />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTwoFAModal(false)}>Cancelar</Button>
                <Button type="submit" data-testid="confirm-enable-2fa-btn">Ativar 2FA</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={disableModal} onOpenChange={setDisableModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Desativar 2FA</DialogTitle>
            <DialogDescription>Confirme a sua identidade para desativar</DialogDescription>
          </DialogHeader>
          <form onSubmit={disable2FA} className="space-y-3">
            <div className="space-y-1"><Label>Confirme a palavra-passe</Label><Input required type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="disable-2fa-password-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDisableModal(false)}>Cancelar</Button>
              <Button type="submit" variant="destructive" data-testid="confirm-disable-2fa-btn">Desativar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
